import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircleIcon, ArrowRightIcon, CalendarClockIcon, FileTextIcon, FlameIcon, LoaderCircleIcon, SendIcon, UserRoundPlusIcon } from 'lucide-react';
import { isOverdue, loadDailyActionQueue, remainingTime, type DailyAction } from '../../lib/workflow';
type Metric = {
  label: string;
  value: number;
  icon: typeof SendIcon;
  tone: 'cyan' | 'amber' | 'rose' | 'slate';
};
export function TodayActionsPanel() {
  const [items, setItems] = useState<DailyAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setItems(await loadDailyActionQueue());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar la cola comercial.');
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const refreshQueue = () => void load();
    window.addEventListener('titan:workflow-updated', refreshQueue);
    return () => window.removeEventListener('titan:workflow-updated', refreshQueue);
  }, [load]);
  const actionable = useMemo(() => items.filter(item => !item.isBlocked && item.workflowState !== 'completed' && item.prospectId), [items]);
  const metrics = useMemo(() => buildMetrics(actionable), [actionable]);
  function startWorkday() {
    if (!actionable.length) return;
    sessionStorage.setItem('titan-workday', JSON.stringify({
      ids: actionable.map(item => item.prospectId),
      index: 0
    }));
    navigate(`/admin/prospectos/${actionable[0].prospectId}`);
  }
  if (isLoading) {
    return <section aria-busy="true" className="rounded-2xl border border-white/10 bg-[#0b1728] p-5 text-sm text-slate-400" data-id="element-584">
        <span className="inline-flex items-center gap-3" data-id="element-585">
          <LoaderCircleIcon className="h-5 w-5 animate-spin text-cyan-200" data-id="element-586" />
          Cargando jornada comercial…
        </span>
      </section>;
  }
  if (error) {
    return <section className="rounded-2xl border border-rose-400/20 bg-rose-400/10 p-5 text-sm text-rose-100" role="alert" data-id="element-587">
        <p data-id="element-588">{error}</p>
        <button className="mt-3 font-semibold underline underline-offset-4" onClick={() => void load()} type="button" data-id="element-589">
          Reintentar
        </button>
      </section>;
  }
  const firstAction = actionable[0];
  return <section aria-labelledby="today-actions-title" className="rounded-2xl border border-white/10 bg-[#0b1728] p-5 shadow-sm sm:p-6" data-id="element-590">
      <div className="flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between" data-id="element-591">
        <div data-id="element-592">
          <p className="font-orbitron text-xs font-bold tracking-[.16em] text-cyan-200" data-id="element-593">
            JORNADA COMERCIAL
          </p>
          <h1 className="mt-1 text-2xl font-bold text-white" id="today-actions-title" data-id="element-594">
            Acciones para avanzar hoy
          </h1>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-400" data-id="element-595">
            Priorizadas por vencimiento, interés y próximos pasos del proceso
            comercial.
          </p>
        </div>
        <button className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-bold text-[#06202a] hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50" disabled={!firstAction} onClick={startWorkday} type="button" data-id="element-596">
          Empezar jornada
          <ArrowRightIcon className="h-4 w-4" data-id="element-597" />
        </button>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-id="element-598">
        {metrics.map(metric => {
        const Icon = metric.icon;
        return <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" key={metric.label} data-id="element-599">
              <dt className={`flex items-center gap-1.5 text-xs font-medium ${toneClass(metric.tone)}`} data-id="element-600">
                <Icon className="h-4 w-4" data-id="element-601" />
                {metric.label}
              </dt>
              <dd className="mt-2 text-2xl font-bold text-white" data-id="element-602">
                {metric.value}
              </dd>
            </div>;
      })}
      </dl>

      <div className="mt-6" data-id="element-603">
        <div className="mb-3 flex items-center justify-between gap-3" data-id="element-604">
          <h2 className="text-base font-bold text-white" data-id="element-605">Prioridad de hoy</h2>
          <button className="min-h-10 text-sm font-semibold text-cyan-200 hover:text-cyan-100" onClick={() => void load()} type="button" data-id="element-606">
            Actualizar
          </button>
        </div>

        {!firstAction ? <p className="rounded-xl border border-dashed border-white/15 p-4 text-sm text-slate-400" data-id="element-607">
            No hay acciones pendientes en la cola comercial.
          </p> : <ol className="divide-y divide-white/10 overflow-hidden rounded-xl border border-white/10" data-id="element-608">
            {actionable.slice(0, 8).map(item => <li key={`${item.prospectId}-${item.stepCode}`} data-id="element-609">
                <button className="flex w-full min-w-0 items-center gap-3 p-4 text-left hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-300" onClick={() => navigate(`/admin/prospectos/${item.prospectId}`)} type="button" data-id="element-610">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${itemTone(item)}`} data-id="element-611" />
                  <span className="min-w-0 flex-1" data-id="element-612">
                    <span className="block truncate text-sm font-semibold text-slate-100" data-id="element-613">
                      {item.prospectName}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-slate-400" data-id="element-614">
                      {dailyActionLabel(item)} ·{' '}
                      {item.responsible || 'Sin asignar'}
                    </span>
                  </span>
                  <span className={`hidden text-xs font-semibold sm:block ${isOverdue(item.dueAt) ? 'text-rose-200' : 'text-slate-400'}`} data-id="element-615">
                    {remainingTime(item.dueAt)}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-slate-500" data-id="element-616" />
                </button>
              </li>)}
          </ol>}
      </div>
    </section>;
}
function buildMetrics(items: DailyAction[]): Metric[] {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  const isToday = (value: string | null) => {
    if (!value) return false;
    const date = new Date(value);
    return date >= start && date < end;
  };
  return [{
    label: 'Prospectos sin contacto',
    value: items.filter(item => ['send_message', 'select_channel', 'complete_information'].includes(item.actionType)).length,
    icon: UserRoundPlusIcon,
    tone: 'slate'
  }, {
    label: 'Mensajes pendientes',
    value: items.filter(item => ['send_message', 'follow_up'].includes(item.actionType)).length,
    icon: SendIcon,
    tone: 'cyan'
  }, {
    label: 'Seguimientos hoy',
    value: items.filter(item => isToday(item.dueAt) && ['follow_up', 'wait_for_response', 'respond', 'qualify'].includes(item.actionType)).length,
    icon: CalendarClockIcon,
    tone: 'amber'
  }, {
    label: 'Vencidos',
    value: items.filter(item => isOverdue(item.dueAt)).length,
    icon: AlertCircleIcon,
    tone: 'rose'
  }, {
    label: 'Prospectos calientes',
    value: items.filter(item => ['hot', 'caliente'].includes(item.interestLevel?.toLowerCase() ?? '')).length,
    icon: FlameIcon,
    tone: 'rose'
  }, {
    label: 'Propuestas pendientes',
    value: items.filter(item => ['send_proposal', 'negotiate'].includes(item.actionType)).length,
    icon: FileTextIcon,
    tone: 'amber'
  }];
}
function dailyActionLabel(item: DailyAction) {
  if (item.actionType === 'complete_information') return 'Preparar primer contacto';
  return item.actionLabel || item.stepName || 'Ver guía de venta';
}
function itemTone(item: DailyAction) {
  if (isOverdue(item.dueAt)) return 'bg-rose-400';
  if (['hot', 'caliente'].includes(item.interestLevel?.toLowerCase() ?? '')) return 'bg-amber-300';
  if (item.actionType === 'wait_for_response') return 'bg-amber-300';
  return 'bg-cyan-300';
}
function toneClass(tone: Metric['tone']) {
  if (tone === 'cyan') return 'text-cyan-200';
  if (tone === 'amber') return 'text-amber-200';
  if (tone === 'rose') return 'text-rose-200';
  return 'text-slate-400';
}
function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}